import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Loader2, Trash2, MapPin, Users, CalendarDays, Link2, RefreshCw, Unplug, ExternalLink, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState, Panel } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg, ymd, addDays, localToday, fmtTime, fmtDateTime } from "@/lib/api";
import { useEmployees } from "@/lib/useEmployees";

const START_H = 7;
const END_H = 21;
const ROW = 48; // px per hour

const startOfWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (x.getDay() + 6) % 7; // monday=0
  return addDays(x, -day);
};

const minutes = (s) => {
  const [h, m] = s.slice(11, 16).split(":").map(Number);
  return h * 60 + m;
};
const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

const sourceLabel = (src) => ({ google_calendar: "Google", apple_calendar: "iCloud", google_ical: "Google (link)", ical: "iCal", microsoft_calendar: "Microsoft" }[src] || "Imported");

const sourceStyle = (src) => {
  // Virtual CEO = primary blue, Google = emerald, Apple = violet, Microsoft = sky, generic ical = amber
  if (!src || src === "virtual_ceo") return { card: "bg-[hsl(210_78%_95%)] border-[hsl(210_78%_80%)]", title: "text-[hsl(210_78%_25%)]", sub: "text-[hsl(210_40%_35%)]" };
  if (src === "google_calendar" || src === "google_ical") return { card: "bg-emerald-50 border-emerald-200", title: "text-emerald-900", sub: "text-emerald-700" };
  if (src === "apple_calendar") return { card: "bg-violet-50 border-violet-200", title: "text-violet-900", sub: "text-violet-700" };
  if (src === "microsoft_calendar") return { card: "bg-sky-50 border-sky-200", title: "text-sky-900", sub: "text-sky-700" };
  return { card: "bg-amber-50 border-amber-200", title: "text-amber-900", sub: "text-amber-700" };
};

const EMPTY = { title: "", date: localToday(), start: "09:00", end: "09:30", attendee_ids: [], location: "", agenda: "" };

function MeetingDialog({ open, onOpenChange, initial, employees, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => { if (open) { setF(initial || EMPTY); setConflicts([]); setSuggestions([]); } }, [open, initial]);
  const editing = !!initial?.meeting_id;
  const imported = !!(initial?.source && initial.source !== "virtual_ceo");

  // Debounced conflict check + nearby-slot suggestions when start/end change
  useEffect(() => {
    if (!open || imported) return;
    if (!f.date || !f.start || !f.end || f.end <= f.start) { setConflicts([]); setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const [{ data: c }, { data: slots }] = await Promise.all([
          api.get("/meetings/conflicts", { params: { start: `${f.date}T${f.start}`, end: `${f.date}T${f.end}`, exclude: initial?.meeting_id } }),
          api.get("/meetings/suggest-slots", { params: { date: f.date, duration: toMinutes(f.end) - toMinutes(f.start), prefer: f.start, exclude: initial?.meeting_id } }),
        ]);
        setConflicts(c);
        setSuggestions(c.length > 0 ? slots : []);
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [open, imported, f.date, f.start, f.end, initial?.meeting_id]);

  const applySlot = (s) => {
    setF((prev) => ({ ...prev, start: s.label_start, end: s.label_end }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (f.end <= f.start) return toast.error("End time must be after start time");
    setBusy(true);
    const body = { title: f.title, start: `${f.date}T${f.start}`, end: `${f.date}T${f.end}`, attendee_ids: f.attendee_ids, location: f.location, agenda: f.agenda };
    try {
      if (editing) await api.patch(`/meetings/${initial.meeting_id}`, body);
      else await api.post("/meetings", body);
      toast.success(editing ? "Meeting updated" : "Meeting scheduled");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/meetings/${initial.meeting_id}`); toast.success("Meeting deleted"); onOpenChange(false); onSaved(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggle = (id) => setF((x) => ({ ...x, attendee_ids: x.attendee_ids.includes(id) ? x.attendee_ids.filter((a) => a !== id) : [...x.attendee_ids, id] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-meeting-dialog">
        <DialogHeader>
          <DialogTitle>{imported ? "Imported event (read-only)" : editing ? "Edit meeting" : "Schedule a meeting"}</DialogTitle>
          <DialogDescription>{imported ? `From ${initial?.source_label || "an external calendar"} \u2014 edit it there and it syncs back.` : "Times are in your local time zone."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <fieldset disabled={imported} className="space-y-4 contents">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="create-meeting-title-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5 sm:col-span-1">
              <Label>Date</Label>
              <DatePicker value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="create-meeting-date-picker" />
            </div>
            <div className="space-y-1.5">
              <Label>Start</Label>
              <Input type="time" required value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="create-meeting-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label>End</Label>
              <Input type="time" required value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="create-meeting-end-input" />
            </div>
          </div>
          {!imported && conflicts.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-amber-900" data-testid="meeting-conflict-warning">
              <p className="text-sm font-medium flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />
                {conflicts.length === 1 ? "1 overlap on this calendar" : `${conflicts.length} overlaps on this calendar`}
              </p>
              <ul className="mt-1.5 space-y-0.5 text-xs">
                {conflicts.slice(0, 4).map((c) => (
                  <li key={c.meeting_id} className="flex items-center gap-2" data-testid="meeting-conflict-item">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    <span className="font-medium">{c.title}</span>
                    <span className="text-amber-800/80">{fmtTime(c.start)}&ndash;{fmtTime(c.end)}{c.source && c.source !== "virtual_ceo" ? ` \u00b7 ${sourceLabel(c.source)}` : ""}</span>
                  </li>
                ))}
                {conflicts.length > 4 && <li className="text-amber-800/80">\u2026 and {conflicts.length - 4} more</li>}
              </ul>
              {suggestions.length > 0 && (
                <>
                  <p className="text-xs text-amber-800/80 mt-2.5 mb-1">Try a nearby open slot:</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="meeting-suggested-slots">
                    {suggestions.map((s) => (
                      <button key={s.start} type="button" onClick={() => applySlot(s)}
                        className="text-xs font-medium bg-white border border-amber-300 hover:bg-amber-100 rounded-full px-2.5 py-1 transition-colors"
                        data-testid="meeting-suggested-slot">
                        {s.label_start}&ndash;{s.label_end}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Attendees</Label>
            <ScrollArea className="h-36 border border-border rounded-lg">
              <div className="p-2 space-y-1">
                {employees.map((e) => (
                  <label key={e.employee_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-muted text-sm cursor-pointer">
                    <Checkbox checked={f.attendee_ids.includes(e.employee_id)} onCheckedChange={() => toggle(e.employee_id)} data-testid="create-meeting-attendee-checkbox" />
                    <span>{e.name}</span><span className="text-xs text-muted-foreground">{e.title}</span>
                  </label>
                ))}
              </div>
            </ScrollArea>
          </div>
          <div className="space-y-1.5">
            <Label>Location</Label>
            <Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Room, address or video link" data-testid="create-meeting-location-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Agenda</Label>
            <Textarea rows={3} value={f.agenda} onChange={(e) => setF({ ...f, agenda: e.target.value })} data-testid="create-meeting-agenda-input" />
          </div>
          </fieldset>
          {imported && initial?.external_link && (
            <a href={initial.external_link} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary inline-flex items-center gap-1" data-testid="meeting-external-link">
              Open in source calendar <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            {editing && !imported && <Button type="button" variant="ghost" className="text-destructive mr-auto" onClick={remove} disabled={busy} data-testid="meeting-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
            {!imported && <Button type="submit" disabled={busy} data-testid="create-meeting-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}{editing ? "Save changes" : "Schedule"}</Button>}
            {imported && <Button type="button" onClick={() => onOpenChange(false)}>Close</Button>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarPage() {
  const [params, setParams] = useSearchParams();
  const [week, setWeek] = useState(startOfWeek(new Date()));
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const { employees } = useEmployees();
  const days = useMemo(() => [...Array(7)].map((_, i) => addDays(week, i)), [week]);
  const today = localToday();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/meetings", { params: { start: ymd(week), end: ymd(addDays(week, 7)) } });
      setMeetings(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [week]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("new")) { setDialog({ open: true, initial: null }); setParams({}, { replace: true }); }
    const connect = params.get("connect");
    const provider = params.get("provider");
    if (connect === "ok") {
      const providerName = provider === "google" ? "Google Calendar" : provider === "microsoft" ? "Microsoft 365" : "Calendar";
      toast.success(`${providerName} connected \u2014 events are syncing.`);
      setConnectionsOpen(true);
      setParams({}, { replace: true });
    } else if (connect === "error") {
      const providerName = provider === "google" ? "Google Calendar" : provider === "microsoft" ? "Microsoft 365" : "the calendar";
      toast.error(`Couldn't connect ${providerName}. Please try again.`);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const openNew = (date, hour) => setDialog({ open: true, initial: { ...EMPTY, date, start: `${String(hour).padStart(2, "0")}:00`, end: `${String(hour).padStart(2, "0")}:30` } });
  const openEdit = (m) => setDialog({ open: true, initial: { meeting_id: m.meeting_id, title: m.title, date: m.start.slice(0, 10), start: m.start.slice(11, 16), end: m.end.slice(11, 16), attendee_ids: m.attendee_ids || [], location: m.location || "", agenda: m.agenda || "", source: m.source, source_label: m.source_label, external_link: m.external_link } });

  const byDay = (d) => meetings.filter((m) => m.start.slice(0, 10) === ymd(d));
  const rangeLabel = `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Calendar" subtitle="Your company's meetings, including those added by the AI CEO and imported from Google or Apple"
        actions={<div className="flex gap-2">
          <Button variant="secondary" onClick={() => setConnectionsOpen(true)} data-testid="calendar-connections-button"><Link2 className="h-4 w-4 mr-1.5" />Connect calendar</Button>
          <Button onClick={() => openNew(today, 9)} data-testid="create-meeting-button"><Plus className="h-4 w-4 mr-1.5" />New meeting</Button>
        </div>} />

      <div className="flex items-center gap-2 mb-4">
        <Button variant="outline" size="icon" className="bg-card" onClick={() => setWeek(addDays(week, -7))} aria-label="Previous week" data-testid="calendar-week-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" className="bg-card" onClick={() => setWeek(startOfWeek(new Date()))} data-testid="calendar-week-today-button">This week</Button>
        <Button variant="outline" size="icon" className="bg-card" onClick={() => setWeek(addDays(week, 7))} aria-label="Next week" data-testid="calendar-week-next-button"><ChevronRight className="h-4 w-4" /></Button>
        <p className="text-sm font-medium ml-2" data-testid="calendar-range-label">{rangeLabel}</p>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* Desktop week grid */}
      <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden" data-testid="calendar-week-grid">
        <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-border">
          <div />
          {days.map((d) => {
            const isT = ymd(d) === today;
            return (
              <div key={ymd(d)} className="px-2 py-2.5 text-center border-l border-border">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{d.toLocaleDateString(undefined, { weekday: "short" })}</p>
                <p className={`text-sm font-semibold mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full ${isT ? "bg-primary text-white" : ""}`}>{d.getDate()}</p>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-[56px_repeat(7,1fr)] max-h-[620px] overflow-y-auto">
          <div>
            {[...Array(END_H - START_H)].map((_, i) => (
              <div key={i} style={{ height: ROW }} className="text-[11px] text-muted-foreground text-right pr-2 pt-0.5 font-mono border-b border-border/60">{String(START_H + i).padStart(2, "0")}:00</div>
            ))}
          </div>
          {days.map((d) => (
            <div key={ymd(d)} className="relative border-l border-border" style={{ height: ROW * (END_H - START_H) }}>
              {[...Array(END_H - START_H)].map((_, i) => (
                <button key={i} type="button" aria-label="Add meeting" onClick={() => openNew(ymd(d), START_H + i)}
                  className="block w-full border-b border-border/60 hover:bg-muted/60 transition-colors" style={{ height: ROW }} />
              ))}
              {byDay(d).map((m) => {
                const top = Math.max(0, (minutes(m.start) - START_H * 60) / 60 * ROW);
                const h = Math.max(22, (minutes(m.end) - minutes(m.start)) / 60 * ROW - 2);
                const style = sourceStyle(m.source);
                return (
                  <button key={m.meeting_id} onClick={() => openEdit(m)} data-testid="calendar-event-card"
                    className={`absolute left-1 right-1 text-left rounded-lg border px-2 py-1 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${style.card}`}
                    style={{ top, height: h }}>
                    <p className={`text-xs font-semibold truncate ${style.title}`}>{m.title}</p>
                    <p className={`text-[11px] truncate ${style.sub}`}>{fmtTime(m.start)}&ndash;{fmtTime(m.end)}{m.source && m.source !== "virtual_ceo" ? ` \u00b7 ${sourceLabel(m.source)}` : ""}</p>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile agenda */}
      <div className="md:hidden space-y-3" data-testid="calendar-agenda">
        {days.map((d) => {
          const list = byDay(d);
          return (
            <div key={ymd(d)} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between">
                <p className={`text-sm font-semibold ${ymd(d) === today ? "text-primary" : ""}`}>{d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openNew(ymd(d), 9)} aria-label="Add meeting"><Plus className="h-4 w-4" /></Button>
              </div>
              {list.length === 0 ? <p className="text-xs text-muted-foreground mt-1">No meetings</p> : (
                <ul className="mt-2 space-y-2">
                  {list.map((m) => {
                    const style = sourceStyle(m.source);
                    return (
                    <li key={m.meeting_id}>
                      <button onClick={() => openEdit(m)} className={`w-full text-left rounded-lg border px-3 py-2 ${style.card}`} data-testid="calendar-agenda-item">
                        <p className={`text-sm font-medium ${style.title}`}>{m.title}</p>
                        <p className={`text-xs ${style.sub}`}>{fmtTime(m.start)}&ndash;{fmtTime(m.end)}{m.source && m.source !== "virtual_ceo" ? ` \u00b7 ${sourceLabel(m.source)}` : ""}</p>
                      </button>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {!loading && meetings.length === 0 && (
        <div className="mt-4 bg-card border border-border rounded-xl"><EmptyState icon={CalendarDays} title="No meetings this week" text="Click any time slot to add one, or ask the AI CEO to schedule for you." /></div>
      )}

      {meetings.length > 0 && (
        <div className="mt-6 bg-card border border-border rounded-xl p-5">
          <h2 className="text-sm font-semibold mb-3">This week's meetings</h2>
          <ul className="divide-y divide-border">
            {meetings.map((m) => (
              <li key={m.meeting_id} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm cursor-pointer hover:bg-muted/50 rounded-md px-1" onClick={() => openEdit(m)} data-testid="calendar-list-item">
                <span className="font-mono text-xs w-36 text-muted-foreground">{m.start.slice(0, 10)} {fmtTime(m.start)}</span>
                <span className="font-medium flex-1 min-w-[160px]">{m.title}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" />{m.attendee_names?.length || 0}</span>
                {m.location && <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{m.location}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <MeetingDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} initial={dialog.initial} employees={employees} onSaved={load} />
      <ConnectionsDialog open={connectionsOpen} onOpenChange={setConnectionsOpen} onChanged={load} />
    </div>
  );
}

function ConnectionsDialog({ open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [icalUrl, setIcalUrl] = useState("");
  const [icalLabel, setIcalLabel] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get("/calendar"); setData(r.data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const connectGoogle = async () => {
    setBusy("google");
    try {
      const { data } = await api.post("/calendar/google/connect", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  const connectMicrosoft = async () => {
    setBusy("microsoft");
    try {
      const { data } = await api.post("/calendar/microsoft/connect", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  const subscribeIcal = async (e) => {
    e.preventDefault();
    if (!icalUrl.trim()) return;
    setBusy("ical");
    try {
      await api.post("/calendar/ical/subscribe", { url: icalUrl.trim(), label: icalLabel.trim() || null });
      toast.success("Calendar subscribed. Importing events now.");
      setIcalUrl(""); setIcalLabel("");
      await load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  const syncNow = async (c) => {
    setBusy(c.connection_id);
    try { const r = await api.post(`/calendar/${c.connection_id}/sync`); toast.success(`Synced \u2014 ${r.data.added} added, ${r.data.updated} updated, ${r.data.removed} removed`); await load(); onChanged?.(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  const disconnect = async (c) => {
    if (!window.confirm(`Disconnect ${c.label}? Imported events will be removed from the app (your source calendar is untouched).`)) return;
    setBusy(c.connection_id);
    try { await api.delete(`/calendar/${c.connection_id}`); toast.success("Disconnected"); await load(); onChanged?.(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="calendar-connections-dialog">
        <DialogHeader>
          <DialogTitle>Connected calendars</DialogTitle>
          <DialogDescription>Import events from Google Calendar or any calendar that offers an iCal/.ics link (Apple iCloud, Outlook, Google iCal link).</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <div className="space-y-5">
            <section className="space-y-2">
              <div className="flex items-start justify-between gap-3 bg-emerald-50/60 border border-emerald-200/60 rounded-xl p-4">
                <div>
                  <p className="text-sm font-semibold">Google Calendar</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Two-way sync. Events you add in the app also appear in Google.</p>
                </div>
                <Button onClick={connectGoogle} disabled={!data.configured.google_calendar || busy === "google"} size="sm" data-testid="connect-google-button">
                  {busy === "google" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Link2 className="h-4 w-4 mr-1.5" />}
                  Connect Google
                </Button>
              </div>
              {!data.configured.google_calendar && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">Google OAuth app isn't configured on this platform yet.</p>
              )}
            </section>

            <section className="space-y-2">
              <div className="flex items-start justify-between gap-3 bg-sky-50/60 border border-sky-200/60 rounded-xl p-4">
                <div>
                  <p className="text-sm font-semibold">Microsoft 365 / Outlook</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Two-way sync for personal and work Microsoft accounts.</p>
                </div>
                <Button onClick={connectMicrosoft} disabled={!data.configured.microsoft_calendar || busy === "microsoft"} size="sm" data-testid="connect-microsoft-button">
                  {busy === "microsoft" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Link2 className="h-4 w-4 mr-1.5" />}
                  Connect Microsoft
                </Button>
              </div>
              {!data.configured.microsoft_calendar && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">Microsoft OAuth app isn't configured on this platform yet.</p>
              )}
            </section>

            <section>
              <p className="text-sm font-semibold">Apple iCloud or any iCal link</p>
              <p className="text-xs text-muted-foreground mb-3">In Apple Calendar, right-click a calendar &rarr; Share Calendar &rarr; Public. Copy the webcal:// link and paste it here (read-only import).</p>
              <form onSubmit={subscribeIcal} className="space-y-2 bg-muted/40 rounded-xl p-3">
                <Input placeholder="webcal://p01-caldav.icloud.com/published/&hellip;" value={icalUrl} onChange={(e) => setIcalUrl(e.target.value)} data-testid="ical-url-input" required />
                <Input placeholder="Name (optional, e.g. Family calendar)" value={icalLabel} onChange={(e) => setIcalLabel(e.target.value)} data-testid="ical-label-input" />
                <Button type="submit" disabled={busy === "ical"} className="w-full" data-testid="ical-subscribe-button">
                  {busy === "ical" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Link2 className="h-4 w-4 mr-1.5" />}
                  Subscribe to iCal feed
                </Button>
              </form>
            </section>

            <section>
              <p className="text-sm font-semibold mb-2">Your connections ({data.connections.length})</p>
              {data.connections.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing connected yet.</p>
              ) : (
                <ul className="space-y-2" data-testid="connections-list">
                  {data.connections.map((c) => {
                    const colorCls = c.provider.startsWith("google") ? "bg-emerald-50 border-emerald-200"
                      : c.provider === "apple_calendar" ? "bg-violet-50 border-violet-200"
                      : c.provider === "microsoft_calendar" ? "bg-sky-50 border-sky-200"
                      : "bg-amber-50 border-amber-200";
                    return (
                    <li key={c.connection_id} className={`rounded-xl border px-3 py-2.5 ${colorCls}`} data-testid="connection-row">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{c.label}</p>
                          <p className="text-xs text-muted-foreground">{c.provider_label} &middot; last sync {c.last_sync ? fmtDateTime(c.last_sync) : "pending"}</p>
                          {c.last_sync_result && !c.last_sync_result.error && (
                            <p className="text-[11px] text-muted-foreground mt-0.5">{c.last_sync_result.total || 0} events &middot; {c.last_sync_result.added || 0} new, {c.last_sync_result.updated || 0} updated</p>
                          )}
                          {c.last_sync_result?.error && <p className="text-[11px] text-rose-700 mt-0.5">{c.last_sync_result.error}</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => syncNow(c)} disabled={busy === c.connection_id} data-testid="connection-sync-button">
                            {busy === c.connection_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => disconnect(c)} disabled={busy === c.connection_id} data-testid="connection-disconnect-button">
                            <Unplug className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
